import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Divider,
    Stack,
    Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Lead, ContactMethod, InterestLevel } from '../../../models/Lead';
import { updateLead } from '../../../services/leadService';
import { addCRMEntry } from '../../../services/crmService';
import { CRMEntry } from '../../../models/CRMEntry';
import { Timestamp } from 'firebase/firestore';

interface ManualOutreachFormProps {
    lead: Lead;
    onLeadUpdate: (updatedLead: Lead) => void;
}

export const ManualOutreachForm: React.FC<ManualOutreachFormProps> = ({ lead, onLeadUpdate }) => {
    const [contacted, setContacted] = useState<boolean>(lead.contacted || false);
    const [contactMethod, setContactMethod] = useState<ContactMethod | ''>(lead.contactMethod || '');
    const [interested, setInterested] = useState<InterestLevel | ''>(lead.interested || '');
    const [meetingScheduled, setMeetingScheduled] = useState<boolean>(lead.meetingScheduled || false);
    const [callNotes, setCallNotes] = useState<string>(lead.callNotes || '');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');

    const isLockedStatus = lead.status === 'In Progress' || lead.status === 'Complete';

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            setErrorMessage('');

            // Prepare the update data
            const updateData: Partial<Lead> = {
                contacted,
                contactMethod: contactMethod || undefined,
                interested: interested || undefined,
                meetingScheduled,
                callNotes: callNotes || undefined,
            };

            // Update the lead
            await updateLead(lead.id, updateData);

            // Update the local lead object
            const updatedLead = { ...lead, ...updateData };
            onLeadUpdate(updatedLead);

            setSuccessMessage('Outreach information updated successfully');

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error('Error updating outreach information:', error);
            setErrorMessage('Failed to update outreach information. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePromoteToCRM = async () => {
        if (isLockedStatus) return;
        try {
            setIsSubmitting(true);
            setErrorMessage('');

            // Create CRM entry first
            const crmData = {
                companyName: lead.business?.name || lead.company || 'Unknown Business',
                contactEmail: null,
                contactPhone: lead.phone || lead.business?.phone || null,
                value: null,
                stage: 'New Lead' as const,
                type: 'lead' as const,
                status: 'active' as const,
                lastActivityAt: Timestamp.fromDate(new Date()),
                lastContactedAt: contacted ? Timestamp.fromDate(new Date()) : null,
                nextFollowUpDate: null,
                expectedCloseDate: null,
                convertedFromLeadId: lead.id,
                notes: callNotes || '',
                assignedTo: lead.assignedTo || null,
                businessId: lead.businessId || null,
                originLeadId: null,
                conversionDate: null,
                cost: null,
                tags: [],
                customFields: {}
            } satisfies Omit<CRMEntry, 'id' | 'createdAt' | 'updatedAt'>;

            // Add CRM entry
            await addCRMEntry(crmData);

            // Update the lead status
            const updateData: Partial<Lead> = {
                contacted,
                contactMethod: contactMethod || undefined,
                interested: interested || undefined,
                meetingScheduled,
                callNotes: callNotes || undefined,
                status: 'In Progress' as const,
                convertedToOpportunity: true,
                convertedAt: new Date(),
            };

            // Update the lead
            await updateLead(lead.id, updateData);

            // Update the local lead object
            const updatedLead = { ...lead, ...updateData };
            onLeadUpdate(updatedLead);

            setSuccessMessage('Lead promoted to CRM successfully');

            // Clear success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error('Error promoting lead to CRM:', error);
            setErrorMessage('Failed to promote lead to CRM. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Manual Outreach
                </Typography>

                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={contacted}
                                    onChange={(e) => setContacted(e.target.checked)}
                                    color="primary"
                                    disabled={isLockedStatus || isSubmitting}
                                />
                            }
                            label="Contacted"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Contact Method</InputLabel>
                            <Select
                                value={contactMethod}
                                onChange={(e) => setContactMethod(e.target.value as ContactMethod)}
                                label="Contact Method"
                                disabled={isLockedStatus || isSubmitting}
                            >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="call">Call</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="text">Text</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Interest Level</InputLabel>
                            <Select
                                value={interested}
                                onChange={(e) => setInterested(e.target.value as InterestLevel)}
                                label="Interest Level"
                                disabled={isLockedStatus || isSubmitting}
                            >
                                <MenuItem value="">Not Set</MenuItem>
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="maybe">Maybe</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={meetingScheduled}
                                    onChange={(e) => setMeetingScheduled(e.target.checked)}
                                    color="primary"
                                    disabled={isLockedStatus || isSubmitting}
                                />
                            }
                            label="Meeting Scheduled"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Call Notes"
                            value={callNotes}
                            onChange={(e) => setCallNotes(e.target.value)}
                            placeholder="Enter notes about the call or contact..."
                            disabled={isLockedStatus || isSubmitting}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handlePromoteToCRM}
                                disabled={isLockedStatus || isSubmitting || !contacted || lead.status === 'In Progress'}
                            >
                                Promote to CRM
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}; 